import React, {useCallback, useEffect, useRef, useState} from 'react'

import Artwork from '../../webgl/Artwork'
import {
  type BrandedDividerRegistration,
  type DitherRegistration,
  type EnterpriseHeroRegistration,
  WebGLContext,
} from '../WebGLContext/WebGLContext'

interface WebGLPageProps {
  children: React.ReactNode
  mode?: 'light' | 'dark'
}

export function WebGLPage({children, mode = 'light'}: WebGLPageProps) {
  const [dithers, setDithers] = useState<DitherRegistration[]>([])
  const [brandeddividers, setBrandedDividers] = useState<BrandedDividerRegistration[]>([])
  const [enterpriseHero, setEnterpriseHero] = useState<EnterpriseHeroRegistration | null>(null)

  const registerDither = useCallback((registration: DitherRegistration) => {
    setDithers(prev => {
      // Check if already registered
      if (prev.find(d => d.id === registration.id)) return prev

      return [...prev, registration]
    })
  }, [])

  const unregisterDither = useCallback((id: string) => {
    setDithers(prev => {
      return prev.filter(dither => dither.id !== id)
    })
  }, [])

  const registerBrandedDivider = useCallback((registration: BrandedDividerRegistration) => {
    setBrandedDividers(prev => {
      // Check if already registered
      if (prev.some(s => s.id === registration.id)) return prev
      return [...prev, registration]
    })
  }, [])

  const unregisterBrandedDivider = useCallback((id: string) => {
    setBrandedDividers(prev => {
      return prev.filter(brandeddivider => brandeddivider.id !== id)
    })
  }, [])

  const registerEnterpriseHero = useCallback((registration: EnterpriseHeroRegistration) => {
    if (enterpriseHero) return
    setEnterpriseHero(prev => {
      return prev ? prev : registration
    })
  }, [])

  const unregisterEnterpriseHero = useCallback((id: string) => {
    setEnterpriseHero(prev => {
      if (prev?.id === id) {
        return null
      }
      return prev
    })
  }, [])

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const artworkRef = useRef<Artwork | null>(null)
  const rAFRef = useRef<number | null>(null)

  useEffect(() => {
    if (canvasRef.current) {
      artworkRef.current = new Artwork({canvas: canvasRef.current})

      if (mode === 'light') {
        artworkRef.current.switchBackgroundColor({isWhite: true})
      } else {
        artworkRef.current.switchBackgroundColor({isWhite: false})
      }

      const handleResize = () => {
        if (artworkRef.current && canvasRef.current) {
          const width = document.body.clientWidth
          const height = window.innerHeight
          artworkRef.current.resize(width, height)
        }
      }

      const handleRaf = () => {
        artworkRef.current?.update()
        rAFRef.current = requestAnimationFrame(handleRaf)
      }

      handleResize()
      window.addEventListener('resize', handleResize)
      handleRaf()

      return () => {
        window.removeEventListener('resize', handleResize)
        if (rAFRef.current) {
          cancelAnimationFrame(rAFRef.current)
        }
      }
    }
  }, [])

  useEffect(() => {
    if (artworkRef.current) {
      artworkRef.current.updateRegisterDither(dithers)
    }
  }, [dithers])

  useEffect(() => {
    if (artworkRef.current) {
      artworkRef.current.updateRegisterBrandedDivider(brandeddividers)
    }
  }, [brandeddividers])

  useEffect(() => {
    if (enterpriseHero) {
      artworkRef.current?.updateRegisterEnterpriseHero(enterpriseHero)
    }
  }, [enterpriseHero])

  const contextValue = {
    registerDither,
    unregisterDither,
    registerBrandedDivider,
    unregisterBrandedDivider,
    registerEnterpriseHero,
    unregisterEnterpriseHero,
    artwork: artworkRef.current,
  }

  return (
    <WebGLContext value={contextValue}>
      <div
        style={{
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <canvas
          ref={canvasRef}
          style={{
            pointerEvents: 'none',
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 0,
          }}
          aria-hidden="true"
          tabIndex={-1}
        />
        {children}
      </div>
    </WebGLContext>
  )
}
